// src/ConveGroupChatArea.tsx

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { GroupMessage, GroupRoom, GroupMember, WsMessageType } from './types/groupChat';
import { groupChatApi } from './api/groupChatApi';
import ConveGroupInput from './ConveGroupInput';
import ConveGroupMembers from './ConveGroupMembers';
import ConveInviteModal from './ConveInviteModal';
import MarkdownRenderer from './MarkdownRenderer';

interface Props {
  room: GroupRoom;
  currentUserId: number;
  currentUserName: string;
  isOnline: (userId: number) => boolean;
  wsMessages: WsMessageType[];
  onMessagesProcessed: (count: number) => void;
  onTyping: (roomId: number, userName: string) => void;
  onRoomLeft: () => void;
}

const ConveGroupChatArea: React.FC<Props> = ({
  room,
  currentUserId,
  currentUserName,
  isOnline,
  wsMessages,
  onMessagesProcessed,
  onTyping,
  onRoomLeft,
}) => {
  const [messages, setMessages] = useState<GroupMessage[]>([]);
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [showMembers, setShowMembers] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [typingText, setTypingText] = useState<string | null>(null);
  const [aiTyping, setAiTyping] = useState(false);
  const [aiStreamingContent, setAiStreamingContent] = useState<Record<string, string>>({});
  const [aiEnabled, setAiEnabled] = useState(room.aiEnabled);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevScrollHeightRef = useRef<number | null>(null);
  const scrollBehaviorRef = useRef<'bottom' | 'restore'>('bottom');
  const loadingMoreRef = useRef(false);
  const isOwner = members.find((m) => m.userId === currentUserId)?.role === 'OWNER';

  // 메시지 초기 로드 (최신 50개, DESC→reverse로 오래된 것 위)
  const loadMessages = useCallback(async () => {
    try {
      setLoadingMessages(true);
      scrollBehaviorRef.current = 'bottom';
      const msgs = await groupChatApi.getMessages(room.id, null, 50);
      const ordered = [...msgs].reverse();
      setMessages(ordered);
      setHasMore(msgs.length >= 50);
      if (ordered.length > 0) {
        groupChatApi.updateRead(room.id, ordered[ordered.length - 1].id).catch(() => {});
      }
    } catch {
      // alert는 api에서 처리
    } finally {
      setLoadingMessages(false);
    }
  }, [room.id]);

  // 이전 메시지 추가 로드 (스크롤 최상단 도달 시)
  const loadMoreMessages = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore || messages.length === 0) return;
    const oldestId = messages[0].id;
    const container = messagesContainerRef.current;
    if (container) {
      prevScrollHeightRef.current = container.scrollHeight;
    }
    scrollBehaviorRef.current = 'restore';
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const older = await groupChatApi.getMessages(room.id, oldestId, 50);
      if (older.length === 0) {
        setHasMore(false);
        return;
      }
      setHasMore(older.length >= 50);
      setMessages((prev) => [...[...older].reverse(), ...prev]);
    } catch {
      // alert는 api에서 처리
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [hasMore, messages, room.id]);

  // 멤버 로드
  const loadMembers = useCallback(async () => {
    try {
      const mems = await groupChatApi.getMembers(room.id);
      setMembers(mems);
    } catch {
      // alert는 api에서 처리
    }
  }, [room.id]);

  useEffect(() => {
    setMessages([]);
    setAiStreamingContent({});
    setAiTyping(false);
    setTypingText(null);
    setAiEnabled(room.aiEnabled);
    setHasMore(false);
    setLoadingMore(false);
    loadingMoreRef.current = false;
    scrollBehaviorRef.current = 'bottom';
    prevScrollHeightRef.current = null;
    loadMessages();
    loadMembers();
  }, [room.id, loadMessages, loadMembers]);

  // 스크롤: 이전 메시지 로드 시 위치 복원, 신규 메시지 수신 시 하단 이동
  useEffect(() => {
    if (scrollBehaviorRef.current === 'restore') {
      if (prevScrollHeightRef.current !== null && messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop =
          messagesContainerRef.current.scrollHeight - prevScrollHeightRef.current;
        prevScrollHeightRef.current = null;
      }
      scrollBehaviorRef.current = 'bottom';
    } else {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, aiStreamingContent]);

  // 스크롤 최상단 감지 → 이전 메시지 로드
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (container.scrollTop === 0 && hasMore && !loadingMoreRef.current) {
      loadMoreMessages();
    }
  }, [hasMore, loadMoreMessages]);

  // WebSocket 메시지 처리 (큐 방식)
  useEffect(() => {
    if (wsMessages.length === 0) return;

    try {
      wsMessages.forEach((wsMsg) => {
        if (wsMsg.type === 'MESSAGE' && wsMsg.message.roomId === room.id) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === wsMsg.message.id)) return prev;
            return [...prev, wsMsg.message];
          });
          // 내가 보낸 메시지가 아니면 읽음 표시
          if (wsMsg.message.senderId !== currentUserId) {
            groupChatApi.updateRead(room.id, wsMsg.message.id).catch(() => {});
          }
        } else if (wsMsg.type === 'AI_CHUNK' && wsMsg.roomId === room.id) {
          try {
            setAiTyping(true);
            const chunkKey = String(wsMsg.messageId);
            setAiStreamingContent((prev) => ({
              ...prev,
              [chunkKey]: (prev[chunkKey] ?? '') + wsMsg.chunk,
            }));
          } catch (err) {
            console.error('[AI_CHUNK] 스트리밍 청크 처리 오류:', err);
            alert('AI 스트리밍 처리 중 오류가 발생했습니다.');
          }
        } else if (wsMsg.type === 'AI_COMPLETE' && wsMsg.message.roomId === room.id) {
          try {
            const completeKey = String(wsMsg.message.id);
            const completeMessage = wsMsg.message;
            console.log('[AI_COMPLETE] 수신:', completeKey,
              '| contentLength:', completeMessage.content?.length,
              '| preview:', completeMessage.content?.substring(0, 100));
            // setTimeout으로 다음 tick에서 처리하여 AI_CHUNK 배치와 겹치지 않게
            setTimeout(() => {
              setAiTyping(false);
              setAiStreamingContent((prev) => {
                console.log('[AI_COMPLETE] aiStreamingContent 제거 전 keys:', Object.keys(prev), '| 제거할 key:', completeKey);
                const next = { ...prev };
                delete next[completeKey];
                return next;
              });
              setMessages((prev) => {
                const filtered = prev.filter((m) => String(m.id) !== completeKey);
                console.log('[AI_COMPLETE] messages 교체: 기존', prev.length, '→', filtered.length + 1, '(msgId:', completeKey, ')');
                return [...filtered, completeMessage];
              });
            }, 50);
          } catch (err) {
            console.error('[AI_COMPLETE] 메시지 교체 오류:', err);
            alert('AI 응답 메시지 처리 중 오류가 발생했습니다.');
          }
        } else if (wsMsg.type === 'SYSTEM' && wsMsg.roomId === room.id) {
          // 시스템 메시지를 messages에 추가 (pseudo-message)
          const sysMsg: GroupMessage = {
            id: Date.now(),
            roomId: room.id,
            senderId: null,
            senderName: 'SYSTEM',
            senderType: 'AI',
            content: wsMsg.content,
            mentionAi: false,
            createdAt: new Date().toISOString(),
            unreadCount: 0,
          };
          setMessages((prev) => [...prev, sysMsg]);
          // AI 오류 메시지인 경우 스트리밍 상태 초기화 및 alert 표시
          if (wsMsg.content.includes('오류')) {
            setAiTyping(false);
            setAiStreamingContent({});
            alert(`AI 오류: ${wsMsg.content}`);
          }
          // 멤버 목록 새로고침 (입장/퇴장 시)
          loadMembers();
        } else if (wsMsg.type === 'TYPING' && wsMsg.roomId === room.id) {
          if (wsMsg.userId === currentUserId) return;
          setTypingText(`${wsMsg.userName}님이 입력 중...`);
          if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
          typingTimerRef.current = setTimeout(() => setTypingText(null), 3000);
        } else if (wsMsg.type === 'READ' && wsMsg.roomId === room.id) {
          // 읽음 표시 업데이트 (메시지 unreadCount 갱신)
          setMessages((prev) =>
            prev.map((m) =>
              m.id <= wsMsg.lastReadMsgId && m.unreadCount > 0
                ? { ...m, unreadCount: Math.max(0, m.unreadCount - 1) }
                : m
            )
          );
        }
      });
    } catch (err) {
      console.error('[GroupWS] 메시지 처리 오류:', err);
      alert('채팅 메시지 처리 중 오류가 발생했습니다.');
    }

    onMessagesProcessed(wsMessages.length);
  }, [wsMessages, room.id, currentUserId, loadMembers, onMessagesProcessed]);

  // 주기적 읽음 표시 전송 (5초 간격, 포커스 상태일 때)
  useEffect(() => {
    const sendReadMark = () => {
      try {
        if (!document.hasFocus()) return;
        const lastMsg = messages[messages.length - 1];
        if (!lastMsg || lastMsg.senderName === 'SYSTEM') return;
        groupChatApi.updateRead(room.id, lastMsg.id).catch((e: unknown) => {
          console.error('[ReadMark] 읽음 표시 API 호출 실패:', e);
        });
      } catch (e) {
        console.error('[ReadMark] 읽음 표시 전송 중 오류:', e);
        alert('읽음 표시 전송 중 오류가 발생했습니다.');
      }
    };

    const intervalId = setInterval(sendReadMark, 5000);
    return () => clearInterval(intervalId);
  }, [room.id, messages]);

  const handleSend = async (content: string, mentionAi: boolean) => {
    try {
      const msg = await groupChatApi.sendMessage(room.id, content, mentionAi);
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
    } catch {
      // groupChatApi.sendMessage()에서 alert 처리됨
    }
  };

  const handleTyping = () => {
    onTyping(room.id, currentUserName);
  };

  const handleKick = async (userId: number) => {
    if (!window.confirm('이 멤버를 강퇴하시겠습니까?')) return;
    try {
      await groupChatApi.removeMember(room.id, userId);
      loadMembers();
    } catch {
      // alert는 api에서 처리
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('채팅방에서 나가시겠습니까?')) return;
    try {
      await groupChatApi.removeMember(room.id, currentUserId);
      onRoomLeft();
    } catch {
      // alert는 api에서 처리
    }
  };

  const handleToggleAi = async () => {
    const newVal = !aiEnabled;
    try {
      await groupChatApi.toggleAi(room.id, newVal);
      setAiEnabled(newVal);
    } catch {
      // alert는 api에서 처리
    }
  };

  const renderMessage = (msg: GroupMessage) => {
    const isMe = msg.senderId === currentUserId;
    const isAi = msg.senderType === 'AI';
    const isSystem = msg.senderName === 'SYSTEM';

    if (isSystem) {
      return (
        <div key={msg.id} className="conve-group-sys-msg">
          {msg.content}
        </div>
      );
    }

    return (
      <div
        key={msg.id}
        className={`conve-group-msg-row ${isMe ? 'mine' : 'theirs'} ${isAi ? 'ai' : ''}`}
      >
        {!isMe && (
          <div className="conve-group-msg-avatar">
            {isAi ? '🤖' : (msg.senderName?.[0]?.toUpperCase() ?? 'U')}
          </div>
        )}
        <div className="conve-group-msg-body">
          {!isMe && (
            <div className="conve-group-msg-sender">
              {isAi ? 'AI' : msg.senderName}
            </div>
          )}
          <div className={`conve-group-msg-bubble ${isAi ? 'ai-bubble' : ''}`}>
            {isAi ? (
              <MarkdownRenderer content={msg.content} streaming={true} />
            ) : (
              <span className="conve-group-msg-text">{msg.content}</span>
            )}
          </div>
          <div className="conve-group-msg-meta">
            <span className="conve-group-msg-time">
              {new Date(msg.createdAt).toLocaleTimeString('ko-KR', {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {msg.unreadCount > 0 && (
              <span className="conve-group-msg-unread">{msg.unreadCount}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="conve-group-chat-area">
      {/* 헤더 */}
      <div className="conve-group-chat-header">
        <div className="conve-group-chat-title">
          <span className="conve-group-room-name">{room.title}</span>
          <span className="conve-group-room-meta">
            {room.memberCount}명
          </span>
        </div>
        <div className="conve-group-header-actions">
          <button
            className={`conve-ai-toggle-btn ${aiEnabled ? 'enabled' : 'disabled'}`}
            onClick={handleToggleAi}
            title={aiEnabled ? 'AI 비활성화' : 'AI 활성화'}
          >
            🤖 {aiEnabled ? 'AI on' : 'AI off'}
          </button>
          <button
            className="conve-member-btn"
            onClick={() => setShowInvite(true)}
            title="멤버 초대"
          >
            👤+
          </button>
          <button
            className="conve-member-btn"
            onClick={() => setShowMembers(!showMembers)}
            title="멤버 목록"
          >
            👥
          </button>
        </div>
      </div>

      <div className="conve-group-chat-body">
        {/* 메시지 영역 */}
        <div className="conve-group-messages" ref={messagesContainerRef} onScroll={handleScroll}>
          {loadingMore && (
            <div className="conve-group-loading">이전 메시지 로딩 중...</div>
          )}
          {!loadingMore && !hasMore && messages.length > 0 && (
            <div className="conve-group-no-more">모든 메시지를 불러왔습니다.</div>
          )}
          {loadingMessages ? (
            <div className="conve-group-loading">메시지 로딩 중...</div>
          ) : messages.length === 0 ? (
            <div className="conve-group-empty">대화를 시작해보세요!</div>
          ) : (
            messages.map((msg) => renderMessage(msg))
          )}

          {/* AI 스트리밍 메시지 */}
          {Object.entries(aiStreamingContent).map(([msgId, content]) => (
            <div key={`ai-stream-${msgId}`} className="conve-group-msg-row theirs ai">
              <div className="conve-group-msg-avatar">🤖</div>
              <div className="conve-group-msg-body">
                <div className="conve-group-msg-sender">AI</div>
                <div className="conve-group-msg-bubble ai-bubble">
                  <MarkdownRenderer content={content} streaming={true} />
                  <span className="conve-ai-cursor">▌</span>
                </div>
              </div>
            </div>
          ))}

          {/* 타이핑 표시 */}
          {typingText && (
            <div className="conve-group-typing">{typingText}</div>
          )}
          {aiTyping && !Object.keys(aiStreamingContent).length && (
            <div className="conve-group-typing">AI가 응답 중...</div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* 멤버 패널 */}
        {showMembers && (
          <ConveGroupMembers
            members={members}
            currentUserId={currentUserId}
            isOnline={isOnline}
            onKick={handleKick}
            onLeave={handleLeave}
            isOwner={isOwner}
          />
        )}
      </div>

      {/* 입력 영역 */}
      <ConveGroupInput
        onSend={handleSend}
        onTyping={handleTyping}
      />

      {/* 초대 모달 */}
      {showInvite && (
        <ConveInviteModal
          roomId={room.id}
          existingMembers={members}
          onClose={() => setShowInvite(false)}
          onInvited={() => {
            setShowInvite(false);
            loadMembers();
          }}
        />
      )}
    </div>
  );
};

export default ConveGroupChatArea;